import React, { useMemo, useState } from "react";
import { Box, Button, Collapse, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { useTranslation } from "react-i18next";
import { exportVoicesPostsForBuffer } from "../../../Api/voicesPosts";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { Features, UserRole } from "../../../Enums/companyStatus";
import { VoicesPostState } from "../../../Enums/voices";
import { getCurrentUserId, hasPermission, hasRole } from "../../../Helpers/authHelpers";
import { VoicesPost, VoicesPostOrder } from "../../../Models/voicesPosts";
import { useVoicesPosts } from "../../../Query/useVoicesPosts";
import { EmptyState } from "../../HomePage/DashboardCard";
import { STATE_COLORS } from "../Calendar/VoicesCalendarPostChip";
import { VoicesSegmented, voicesToolbarSx } from "../Common/VoicesFilterParts";
import VoicesInput from "../Common/VoicesInput";
import VoicesPostRows from "./VoicesPostRows";

interface PostGroup {
  key: string;
  states: VoicesPostState[];
  tone: string;
}

const POST_GROUPS: PostGroup[] = [
  {
    key: "group_preparation",
    states: [VoicesPostState.DRAFT, VoicesPostState.IN_APPROVAL],
    tone: STATE_COLORS[VoicesPostState.DRAFT],
  },
  {
    key: "group_scheduled",
    states: [VoicesPostState.SCHEDULED],
    tone: STATE_COLORS[VoicesPostState.SCHEDULED],
  },
  {
    key: "group_published",
    states: [VoicesPostState.PUBLISHED],
    tone: STATE_COLORS[VoicesPostState.PUBLISHED],
  },
  {
    key: "group_failed",
    states: [VoicesPostState.FAILED],
    tone: STATE_COLORS[VoicesPostState.FAILED],
  },
];

const ALL_GROUPS = "all";
const ALL_CAMPAIGNS = "all";
const EVERYONE = "everyone";
const EXPORTABLE_STATES = [VoicesPostState.SCHEDULED, VoicesPostState.FAILED];
const GROUP_PAGE = 10;
const WRITTEN_BY_ME = "mine";

const targetKey = (post: VoicesPost) => `${post.target.kind}:${post.target.id}`;

interface Props {
  campaignId?: number;
  actions?: React.ReactNode;
}

const VoicesPostBrowser: React.FC<Props> = ({ campaignId, actions }) => {
  const { t } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const [order, setOrder] = useState<VoicesPostOrder>("newest");
  const { data: posts = [], isLoading } = useVoicesPosts(order, campaignId);

  const [campaign, setCampaign] = useState(ALL_CAMPAIGNS);
  const [person, setPerson] = useState(EVERYONE);
  const [group, setGroup] = useState(ALL_GROUPS);
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [limits, setLimits] = useState<Record<string, number>>({});
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const seesEveryone = hasPermission(Features.VOICES_MARKETING) || hasRole(UserRole.ADMIN);
  const currentUserId = getCurrentUserId();
  const canExport =
    campaignId === undefined &&
    (seesEveryone
      ? hasPermission(Features.VOICES_MARKETING)
      : hasPermission(Features.VOICES_POSTING));
  const exportTarget: string | null | undefined = seesEveryone
    ? person.includes(":")
      ? person
      : undefined
    : null;

  const exportable = (post: VoicesPost) =>
    EXPORTABLE_STATES.includes(post.state) &&
    !!post.scheduled_at &&
    new Date(post.scheduled_at) > new Date() &&
    (exportTarget === null || targetKey(post) === exportTarget);

  const exportCsv = async () => {
    if (exportTarget === undefined) return;
    setExporting(true);
    try {
      await exportVoicesPostsForBuffer(exportTarget, selectedIds);
    } catch {
      // the snack already shows what went wrong
    } finally {
      setExporting(false);
    }
  };

  const campaigns = useMemo(() => {
    const unique = new Map<number, string>();
    posts.forEach(
      (post) => post.campaign_id && unique.set(post.campaign_id, post.campaign_name ?? ""),
    );
    return Array.from(unique, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [posts]);

  const targets = useMemo(() => {
    const unique = new Map<string, string>();
    posts.forEach((post) => unique.set(targetKey(post), post.target.name));
    return Array.from(unique, ([key, name]) => ({ key, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [posts]);

  const filtered = posts.filter(
    (post) =>
      (campaign === ALL_CAMPAIGNS || String(post.campaign_id) === campaign) &&
      (person === EVERYONE ||
        (person === WRITTEN_BY_ME
          ? post.author?.id === currentUserId
          : targetKey(post) === person)),
  );

  const groupPosts = (item: PostGroup) =>
    filtered.filter((post) => item.states.includes(post.state));

  const shownGroups = POST_GROUPS.filter((item) => group === ALL_GROUPS || item.key === group)
    .map((item) => ({ ...item, posts: groupPosts(item) }))
    .filter((item) => item.posts.length);

  const resetPaging = () => {
    setLimits({});
    setSelectedIds([]);
  };

  const toggleCollapsed = (key: string) =>
    setCollapsed((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );

  const renderGroup = (item: PostGroup & { posts: VoicesPost[] }) => {
    const open = !collapsed.includes(item.key);
    const limit = limits[item.key] ?? GROUP_PAGE;
    const remaining = item.posts.length - limit;
    return (
      <Box key={item.key}>
        <Box
          component="button"
          type="button"
          onClick={() => toggleCollapsed(item.key)}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.75,
            mb: 0.5,
            border: 0,
            borderRadius: "8px",
            background: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
            "&:hover": { backgroundColor: color.gray50 },
          }}
        >
          <Box
            aria-hidden
            sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: item.tone }}
          />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: color.gray900 }}>
            {t(item.key)}
          </Typography>
          <Typography sx={{ fontSize: 13, color: color.gray500 }}>{item.posts.length}</Typography>
          <ExpandMoreIcon
            sx={{
              ml: "auto",
              fontSize: 20,
              color: color.gray400,
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        </Box>
        <Collapse in={open} timeout="auto">
          <VoicesPostRows
            posts={item.posts.slice(0, limit)}
            emptyText=""
            showCampaign={campaignId === undefined}
            selectable={canExport && exportTarget !== undefined ? exportable : undefined}
            selectedIds={selectedIds}
            onToggle={(postId) =>
              setSelectedIds((current) =>
                current.includes(postId)
                  ? current.filter((id) => id !== postId)
                  : [...current, postId],
              )
            }
          />
          {remaining > 0 && (
            <Button
              onClick={() => setLimits({ ...limits, [item.key]: limit + GROUP_PAGE })}
              sx={{ mt: 0.5, textTransform: "none", fontSize: 13, color: workspace.accent }}
            >
              {t("engage_more", {
                count: Math.min(remaining, GROUP_PAGE),
                total: remaining,
              })}
            </Button>
          )}
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      <Box sx={voicesToolbarSx}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          {campaignId === undefined && (
            <VoicesInput
              select
              size="small"
              value={campaign}
              onChange={(e) => {
                setCampaign(e.target.value);
                resetPaging();
              }}
              sx={{ width: 200 }}
            >
              <MenuItem value={ALL_CAMPAIGNS}>{t("calendar_allCampaigns")}</MenuItem>
              {campaigns.map((item) => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.name}
                </MenuItem>
              ))}
            </VoicesInput>
          )}
          {seesEveryone && (
            <VoicesInput
              select
              size="small"
              value={person}
              onChange={(e) => {
                setPerson(e.target.value);
                resetPaging();
              }}
              sx={{ width: 220 }}
            >
              <MenuItem value={EVERYONE}>{t("posts_everyone")}</MenuItem>
              <MenuItem value={WRITTEN_BY_ME}>{t("posts_writtenByMe")}</MenuItem>
              {targets.map((target) => (
                <MenuItem key={target.key} value={target.key}>
                  {t("posts_for", { name: target.name })}
                </MenuItem>
              ))}
            </VoicesInput>
          )}
          <VoicesSegmented
            value={group}
            onChange={(value) => {
              setGroup(value);
              resetPaging();
            }}
            segments={[
              { value: ALL_GROUPS, label: t("posts_all"), count: filtered.length },
              ...POST_GROUPS.map((item) => ({
                value: item.key,
                label: t(item.key),
                count: groupPosts(item).length,
                tone: item.tone,
              })),
            ]}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Tooltip title={t("posts_orderHint")}>
            <Button
              startIcon={<SwapVertIcon />}
              onClick={() => {
                setOrder(order === "newest" ? "oldest" : "newest");
                resetPaging();
              }}
              sx={{ textTransform: "none", fontWeight: 600, color: color.gray700 }}
            >
              {t(order === "newest" ? "posts_newestFirst" : "posts_oldestFirst")}
            </Button>
          </Tooltip>
          {canExport && (
            <Tooltip
              title={exportTarget === undefined ? t("export_pickTarget") : t("export_hint")}
            >
              <span>
                <Button
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={exportTarget === undefined || exporting}
                  onClick={exportCsv}
                  sx={{ textTransform: "none", fontWeight: 600, color: color.gray700 }}
                >
                  {selectedIds.length
                    ? t("export_selected", { count: selectedIds.length })
                    : t("export_button")}
                </Button>
              </span>
            </Tooltip>
          )}
          {actions}
        </Box>
      </Box>

      {!isLoading &&
        (shownGroups.length ? (
          <Stack spacing={2}>{shownGroups.map(renderGroup)}</Stack>
        ) : (
          <EmptyState>
            {campaignId !== undefined && !posts.length ? t("noPosts") : t("posts_empty")}
          </EmptyState>
        ))}
    </>
  );
};

export default VoicesPostBrowser;
